import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { HUB_SIGN, type HubSpot } from "../hubs";
import type { MapLook } from "../maps";
import { hubSignTexture } from "./textures";
import { inked, toon } from "./toon";

// Challenge hub signs (#87 part 3): an overhead gantry at each hub, two posts
// either side of the ring holding a "TIER N / CHALLENGES" banner high above
// the hub's badge, so you can spot a hub from across the park and roll
// through it. Outdoors it's a steel street sign in the hub's magenta; in an
// indoor park it's a plywood board on timber posts, spray-painted. Built
// with the park (`buildLevelMeshes`), so it follows the art style. Where the
// posts go is `hubSignPosts` (sim-free, tested in `hubs.test.ts`).

const HUB = "#f72585";
const INK = "#1b1530";
const BOARD_D = 0.12;

interface SignLook {
  post: THREE.MeshStandardMaterial;
  /** Square timber (indoors) or round steel (outdoors). */
  postGeo: THREE.BufferGeometry;
  base: { geo: THREE.BufferGeometry; mat: THREE.MeshStandardMaterial };
  board: THREE.MeshStandardMaterial;
  /** The banner face: board color, ink and trim. */
  face: [bg: string, ink: string, trim: string];
}

function signLook(look: MapLook): SignLook {
  const h = HUB_SIGN.bannerTop;
  const r = HUB_SIGN.postR;
  if (look.indoor) {
    return {
      post: toon("#8a6a48", { roughness: 0.8 }, "plywood"),
      postGeo: new THREE.BoxGeometry(r * 2, h, r * 2).translate(0, h / 2, 0),
      base: { geo: new THREE.BoxGeometry(0.4, 0.2, 0.4).translate(0, 0.1, 0), mat: toon("#9c9a96", { roughness: 0.95 }, "concrete") },
      board: toon("#b07a48", { roughness: 0.75 }, "plywood"),
      face: ["#f0c17a", INK, HUB],
    };
  }
  return {
    post: toon("#5b6272", { roughness: 0.4, metalness: 0.3 }, "paintedMetal"),
    postGeo: new THREE.CylinderGeometry(r, r, h, 12).translate(0, h / 2, 0),
    base: { geo: new THREE.CylinderGeometry(0.16, 0.18, 0.03, 16).translate(0, 0.015, 0), mat: toon("#5b6272", { roughness: 0.4, metalness: 0.3 }, "paintedMetal") },
    board: toon(HUB, { roughness: 0.5 }, "paint"),
    face: [INK, "#ffffff", HUB],
  };
}

/** The signs for a map's hubs, in world space; an empty group for a map without hubs. */
export function buildHubSigns(hubs: readonly HubSpot[], look: MapLook): THREE.Group {
  const root = new THREE.Group();
  root.name = "hub-signs";
  if (hubs.length === 0) return root;
  const s = signLook(look);
  const { postOffset, bannerBottom, bannerTop } = HUB_SIGN;
  const bannerH = bannerTop - bannerBottom;
  const bannerW = postOffset * 2 + 0.3;
  const boardGeo = new RoundedBoxGeometry(bannerW, bannerH, BOARD_D, 2, 0.03);
  const faceGeo = new THREE.PlaneGeometry(bannerW - 0.12, bannerH - 0.12);
  const [bg, ink, trim] = s.face;

  for (const h of hubs) {
    const sign = new THREE.Group();
    // Local +x runs across the hub (post to post), local +z the way a skater sent there faces.
    sign.position.set(h.x, 0, h.z);
    sign.rotation.y = h.yaw;
    for (const side of [-1, 1]) {
      const post = inked(s.postGeo, s.post, 0.01);
      post.position.x = side * postOffset;
      const base = new THREE.Mesh(s.base.geo, s.base.mat);
      base.position.x = side * postOffset;
      base.receiveShadow = true;
      sign.add(post, base);
    }
    const board = inked(boardGeo, s.board, 0.015);
    board.position.y = (bannerBottom + bannerTop) / 2;
    board.receiveShadow = true;
    sign.add(board);
    // The face, readable from both sides: lit a little from within so it reads in the shade or indoors.
    const map = hubSignTexture(h.tier, bg, ink, trim);
    const faceMat = toon(map ? "#ffffff" : bg, map ? { map, emissive: "#ffffff", emissiveMap: map, emissiveIntensity: 0.35, roughness: 0.6 } : { roughness: 0.6 }, "paint");
    for (const side of [-1, 1]) {
      const face = new THREE.Mesh(faceGeo, faceMat);
      face.position.set(0, board.position.y, side * (BOARD_D / 2 + 0.004));
      if (side < 0) face.rotation.y = Math.PI;
      sign.add(face);
    }
    root.add(sign);
  }
  return root;
}
